import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { BaseTextField } from '@user/components/Formik'
import { Field, Form, useFormikContext } from 'formik'
import { DeleteSalesTeamFormModel } from './typings'
import { ISalesTeam } from '@user/store/salesTeam/typings'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'

const getStyles = (theme: Theme) => ({
  label: {
    fontFamily: 'Source Sans Pro Semibold',
    marginBottom: theme.spacing(1),
  },

  title: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: '16px',
    marginBottom: theme.spacing(1),
  },

  cancelBtn: {
    marginRight: theme.spacing(1),
  },
})

interface Props {
  onCancel: () => void
  salesTeam: ISalesTeam
}

export const DeleteSalesTeamFormFields: FC<Props> = (props: Props) => {
  const { onCancel, salesTeam } = props
  const [disableSave, setDisableSave] = useState<boolean>(true)

  // Hides company default sales team functionality
  const hideCompanyDefaultSalesTeam = true

  const { isValid, isValidating, isSubmitting } =
    useFormikContext<DeleteSalesTeamFormModel>()

  const salesTeams = useSelector(selectAllSalesTeams)

  const filteredSalesTeams = useMemo(() => {
    return salesTeams.filter((x) => x.id != salesTeam.id)
  }, [salesTeam.id, salesTeams])

  useEffect(() => {
    setDisableSave(!isValid || isValidating || isSubmitting)
  }, [isValid, isValidating, isSubmitting])

  const headerText = useMemo(() => {
    return `Please select a new ${salesTeam.branchDefault ? 'branch' : ''}${
      salesTeam.branchDefault && salesTeam.tenantDefault ? ' and ' : ''
    }${salesTeam.tenantDefault ? 'company' : ''} default.`
  }, [salesTeam])
  const styles = getStyles(useTheme())
  return (
    <Form>
      <Box mb={3}>{headerText}</Box>
      <Grid container spacing={3}>
        {salesTeam.branchDefault == true && (
          <Grid item xs={12}>
            <Typography sx={styles.label}>
              Default Sales Team for {salesTeam.branchName}
            </Typography>
            <Field
              name="branchDefault"
              label="Branch Default"
              component={BaseTextField}
              select
              required
            >
              <MenuItem value={-1}>No Default</MenuItem>
              {filteredSalesTeams
                .filter((x) => x.branchId === salesTeam.branchId)
                .map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
            </Field>
          </Grid>
        )}

        {!hideCompanyDefaultSalesTeam && salesTeam.tenantDefault == true && (
          <Grid item xs={12}>
            <Typography sx={styles.label}>
              Company Default Sales Team
            </Typography>
            <Field
              name="tenantDefault"
              label="Company Default"
              component={BaseTextField}
              select
              required
            >
              <MenuItem value={-1}>No Default</MenuItem>
              {filteredSalesTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Field>
            <Box mt={3}>
              <Divider />
            </Box>
          </Grid>
        )}
      </Grid>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button sx={styles.cancelBtn} onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={disableSave}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
      </Box>
    </Form>
  )
}

DeleteSalesTeamFormFields.displayName = 'DeleteSalesTeamFormFields'
